/**
 * BEWARE:
 * If you need to rename any file, ensure that the name change is reflected in BOTH the public web and the self-care
 * applications to avoid breaking links or causing inconsistencies. The naming must be the same across both projects.
 * Before proceeding with any renaming, investigate its usage in both contexts and make sure the change is implemented
 * consistently.
 */

import { Locale } from '@/types/locale';

/**
 * Represents a collection of archived documents.
 * Each document has a file path, a date indicating its validity, and an optional custom label.
 *
 * @typedef {Object[]} ArchivedDocuments
 * @property {string} path - The file path of the document.
 * @property {string} date - The date until which the document is valid.
 * @property {string} [customLabel] - An optional custom label for the document that bypasses the src/lang/[lang]/termsAndConditions.json translations.
 */
type ArchivedDocuments = { path: string; date: string; customLabel?: string }[];

/**
 * Note that these deprecated files also include terms and conditions for all products, such as MINI, MAXI, Pay in three, etc.
 * This might give the impression that they are duplicated across categories, but this is actually the intended behavior.
 */
const CS_OLD_TERMS_AND_CONDITIONS: ArchivedDocuments = [
	{
		path: '/files/cs/Skippay_Vseobecne_obchodni_podminky_platne_do_03-10-2024.pdf',
		date: '3. 10. 2024',
	},
	{
		path: '/files/cs/Skippay_Vseobecne_obchodni_podminky_platne_do_06-3-2024.pdf',
		date: '6. 3. 2024',
	},
	{
		path: '/files/Skippay_Vseobecne_obchodni_podminky_platne_do_30-11-2023.pdf',
		date: '30. 11. 2023',
	},
	{
		path: '/files/Skippay_Vseobecne_obchodni_podminky_platne_do_30-6-2023.pdf',
		date: '30. 6. 2023',
	},
	{
		path: '/files/MP_VOP_platne_do_5-10-2022.pdf',
		date: '5. 10. 2022',
	},
	{
		path: '/files/MP_VOP_platne_do_14-10-2021.pdf',
		date: '14. 10. 2021',
	},
	{
		path: '/files/MP_VOP_platne_do_31-3-2021.pdf',
		date: '31. 3. 2021',
	},
	{
		path: '/files/MP_VOP_platne_do_6-1-2021.pdf',
		date: '6. 1. 2021',
	},
	{
		path: '/files/MP_VOP_platne_do_31-7-2020.pdf',
		date: '31. 7. 2020',
	},
	{
		path: '/files/MP_VOP_platne_do_21-5-2019.pdf',
		date: '21. 5. 2019',
	},
	{
		path: '/files/MP_VOP_platne_do_9-4-2019.pdf',
		date: '9. 4. 2019',
	},
	{
		path: '/files/MP_VOP_platne_do_10-1-2019.pdf',
		date: '10. 1. 2019',
	},
	{
		path: '/files/MP_VOP_platne_do_20-9-2018.pdf',
		date: '20. 9. 2018',
	},
	{
		path: '/files/MP_VOP_platne_do_31-7-2018.pdf',
		date: '31. 7. 2018',
	},
];

const SK_OLD_TERMS_AND_CONDITIONS_PAY_IN_THREE: ArchivedDocuments = [
	{
		path: '/files/sk/Skippay_Podmienky_nakupu_na_tretinu_do_22-04-2025.pdf',
		date: '22. 4. 2025',
		customLabel: 'Podmienky Nákupu na Tretinu platné do 22. 4. 2025 (PDF)',
	},
	{
		path: '/files/sk/Skippay_Podmienky_nakupu_na_tretinu_do_17-02-2025.pdf',
		date: '17. 2. 2025',
		customLabel: 'Podmienky Nákupu na Tretinu platné do 17. 2. 2025 (PDF)',
	},
];

const CZ_OLD_TERMS_AND_CONDITIONS_PAY_IN_THREE: ArchivedDocuments = [
	{
		path: '/files/cs/Skippay_Podminky_Nakupu_na_Tretinu_do_30-10-2025.pdf',
		date: '30. 10. 2025',
		customLabel: 'Podmínky Nákupu na Třetinu platné do 30. 10. 2025 (PDF)',
	},
	{
		path: '/files/cs/Skippay_Podminky_Nakupu_na_Tretinu_do_22-4-2025.pdf',
		date: '22. 4. 2025',
		customLabel: 'Podmínky Nákupu na Třetinu platné do 22. 4. 2025 (PDF)',
	},
	{
		path: '/files/cs/Skippay_Podminky_Nakupu_na_Tretinu_do_17-02-2025.pdf',
		date: '17. 2. 2025',
		customLabel: 'Podmínky Nákupu na Třetinu platné do 17. 2. 2025 (PDF)',
	},
];

const CZ_OLD_TERMS_AND_CONDITIONS_MAXI: ArchivedDocuments = [
	{
		path: '/files/cs/Skippay_Podminky_balicku_MAXI_do_26-02-2026.pdf',
		date: '26. 2. 2026',
		customLabel: 'Podmínky balíčku MAXI platné do 26. 2. 2026 (PDF)',
	},
	{
		path: '/files/cs/Skippay_Podminky_balicku_MAXI_do_16-12-2025.pdf',
		date: '16. 12. 2025',
		customLabel: 'Podmínky balíčku MAXI platné do 16. 12. 2025 (PDF)',
	},
	{
		path: '/files/cs/Skippay_Podminky_balicku_MAXI_do_22-04-2025.pdf',
		date: '22. 4. 2025',
		customLabel: 'Podmínky balíčku MAXI platné do 22. 4. 2025 (PDF)',
	},
];

const CZ_OLD_TERMS_AND_CONDITIONS_MINI: ArchivedDocuments = [
	{
		path: '/files/cs/Skippay_Podminky_balicku_MINI_do_22-04-2025.pdf',
		date: '22. 4. 2025',
		customLabel: 'Podmínky balíčku MINI platné do 22. 4. 2025 (PDF)',
	},
];

const OLD_PRICINGS: ArchivedDocuments = [
	{
		path: 'files/cs/Skippay_Cenik_do_26-2-2026.pdf',
		date: '26. 2. 2026',
	},
	{
		path: 'files/cs/Skippay_Cenik_do_16-12-2025.pdf',
		date: '16. 12. 2025',
	},
	{
		path: 'files/cs/Skippay_Cenik_do_13-10-2025.pdf',
		date: '13. 10. 2025',
	},
	{
		path: 'files/cs/Skippay_Cenik_do_22-4-2025.pdf',
		date: '22. 4. 2025',
	},
	{
		path: '/files/cs/Skippay_Cenik_do_03-10-2024.pdf',
		date: '3. 10. 2024',
	},
	{
		path: '/files/cs/Skippay_Cenik_do_06-3-2024.pdf',
		date: '6. 3. 2024',
	},
	{
		path: '/files/Skippay_Cenik_do_30-11-2023.pdf',
		date: '30. 11. 2023',
	},
	{
		path: '/files/Skippay_Cenik_do_30-6-2023.pdf',
		date: '30. 6. 2023',
	},
	{
		path: '/files/Skippay_Cenik_do_5-12-2022.pdf',
		date: '5. 12. 2022',
	},
	{
		path: '/files/MP_cenik_do_5-10-2022.pdf',
		date: '5. 10. 2022',
	},
	{
		path: '/files/MP_cenik_do_30-1-2022.pdf',
		date: '30. 1. 2022',
	},
	{
		path: '/files/MP_cenik_do_14-10-2021.pdf',
		date: '14. 10. 2021',
	},
	{
		path: '/files/MP_cenik_do_30-4-2021.pdf',
		date: '30. 4. 2021',
	},
];

const OLD_CARD_TERMS_AND_CONDITIONS: ArchivedDocuments = [
	{
		path: '/files/cs/Skippay_Podminky_pro_pouzivani_Skip_karty_platne_do_06-3-2024.pdf',
		date: '6. 3. 2024',
	},
	{
		path: '/files/Skippay_Podminky_pro_pouzivani_Skip_karty_do_30-11-2023.pdf',
		date: '30. 11. 2023',
	},
	{
		path: '/files/MP_OP_ke_karte_do_5-10-2022.pdf',
		date: '5. 10. 2022',
	},
	{
		path: '/files/MP_OP_ke_karte_do_14-10-2021.pdf',
		date: '14. 10. 2021',
	},
	{
		path: '/files/MP_OP_ke_karte_do_31-3-2021.pdf',
		date: '31. 3. 2021',
	},
	{
		path: '/files/MP_OP_ke_karte_do_6-1-2021.pdf',
		date: '6. 1. 2021',
	},
	{
		path: '/files/cs/Skippay_Podminky_pro_pouzivani_Skip_karty_do_06-3-2024.pdf',
		date: '6. 3. 2024',
	},
];

const CS_FILES = {
	currentPricingDocument: 'files/Skippay_Cenik.pdf',
	deprecatedPricingDocument: 'files/cs/Skippay_Cenik_do_22-4-2025.pdf',
	oldPricings: OLD_PRICINGS,
	cashbackRules: '/files/cs/Pravidla-Cashback-programu-Skip-Pay.pdf',
	pre_contractual_information_GTC: '/files/cs/Predsmluvni_informace_VPP.pdf',
	loyaltyProgramRules: '/files/cs/Skippay_Pravidla_vernostniho_programu.pdf',
	conditionsForObtainingExtraRewardPoints:
		'/files/cs/Skippay_Podminky_pro_odmenu_za_platbu_s_CSOB.pdf',
	conditionsFor12x100: 'files/cs/SP_Podminky_akce_Sleva_12100_Kc_na_vyuctovani.pdf',
	complaintsProcedure: '/files/cs/Skippay_Reklamacni_rad.pdf',
	yourRightsWhenMakingPaymentsInEurope: '/files/cs/leaflet-your-rights-payments-eu_cs.pdf',
	insuranceTerms: '/files/cs/Skippay_Pojisteni_prodlouzene_zaruky.pdf',
	termsAccessibility: '/files/cs/Skippay_Prohlaseni_o_pristupnosti.pdf',
	termsAndConditions: '/files/cs/Skippay_Vseobecne_obchodni_podminky.pdf',
	termsAndConditionsDeferredPayment: '/files/Skippay_Podminky_Odlozene_platby.pdf',
	termsAndConditionsPayInThree: '/files/cs/Skippay_Podminky_Nakupu_na_Tretinu_CZ.pdf',
	termsAndConditionsMini: '/files/cs/Skippay_Podminky_balicku_MINI.pdf',
	termsAndConditionsMaxi: '/files/cs/Skippay_Podminky_balicku_MAXI.pdf',
	termsAndConditionsMaxiDeprecated: '/files/cs/Skippay_Podminky_balicku_MAXI_od_17-12-2025.pdf',
	termsAndConditionsMaxiCurrent: '/files/Skippay_Podminky_balicku_MAXI_od_23-04-2025.pdf',
	termsAndConditionsMaxiSampleContract: '/files/cs/Skippay_Vzor_Smlouvy_o_balicku_MAXI.pdf',
	termsAndConditionsMaxiSamplePreContractualInformation:
		'/files/cs/Skippay_Vzor_Predsmluvnich_informaci_k_balicku_MAXI.pdf',
	oldTermsAndConditions: CS_OLD_TERMS_AND_CONDITIONS,
	oldTermsAndConditionsPayInThree: [
		...CZ_OLD_TERMS_AND_CONDITIONS_PAY_IN_THREE,
		...CS_OLD_TERMS_AND_CONDITIONS,
	],
	oldTermsAndConditionsDeferredPayment: [
		{
			path: '/files/cs/Skippay_Podminky_Odlozene_platby_do_30-10-2025.pdf',
			date: '30. 10. 2025',
			customLabel: 'Podmínky Odložené platby platné do 30. 10. 2025 (PDF)',
		},
		{
			path: '/files/cs/Skippay_Podminky_Odlozene_platby_do_17-02-2025.pdf',
			date: '17. 2. 2025',
			customLabel: 'Podmínky Odložené platby platné do 17. 2. 2025 (PDF)',
		},
		...CS_OLD_TERMS_AND_CONDITIONS,
	],
	oldTermsAndConditionsMini: [...CZ_OLD_TERMS_AND_CONDITIONS_MINI, ...CS_OLD_TERMS_AND_CONDITIONS],
	oldTermsAndConditionsMaxi: [...CZ_OLD_TERMS_AND_CONDITIONS_MAXI, ...CS_OLD_TERMS_AND_CONDITIONS],
	oldCardTermsAndConditions: OLD_CARD_TERMS_AND_CONDITIONS,
	informationMemorandum: '/files/cs/Skippay_Informacni_memorandum.pdf',
	informationMemorandumPartners: '/files/cs/Skippay_Informacni_memorandum_partneri.pdf',
	informationMemorandumSID: '/files/cs/Skippay_TelcoScore_Informacni_memorandum.pdf',
	informationMemorandumNRKI: '/files/cs/Skippay_NRKI_Informacni_memorandum.pdf',
	informationMemorandumCareer:
		'/files/cs/Skippay_Informacni_memorandum_pro_uchazece_o_zamestnani.pdf',
	personalDataProtection: 'https://www.mcribis.cz/ochrana-osobnich-udaju/',
	NRKIInformationMemorandum: '/files/cs/Skippay_NRKI_notifikace.pdf',
	xmasConditions: '/files/cs/Skippay_Podminky_akce_nakupte_darky_ted_zaplatte_az_po_vanocich.pdf',
	springConditions: '/files/cs/Skippay_Podminky_akce_vy_nakupujete_my_to_platime.pdf',
	deprecatedInsurance: {
		insurance: {
			conditions: '/files/cs/Skippay_PP_pojisteni_nahodneho_poskozeni_nebo_odcizeni_veci.pdf',
			ipid: '/files/cs/Skippay_IPID_pojisteni_nahodneho_poskozeni_nebo_odcizeni_veci.pdf',
			infolist: '/files/cs/Skippay_Infolist_pojisteni_nahodneho_poskozeni_nebo_odcizeni_veci.pdf',
		},
		extendedWarranty: {
			conditions: '/files/cs/Skippay_VPP_pojisteni_prodlouzene_zaruky.pdf',
			ipid: '/files/cs/Skippay_IPID_pojisteni_prodlouzene_zaruky.pdf',
			infolist: '/files/cs/Skippay_Infolist_pojisteni_prodlouzene_zaruky.pdf',
		},
	},
	ethicalCodex: '/files/cs/Skippay_Eticky_kodex.pdf',
	criminalActivity: '/files/cs/Skippay_Nulova_tolerance_k_trestne_cinnosti.pdf',
	conditionsPromoTryItForFree:
		'/files/cs/Skippay_Podminky_akce_posunuti_splatnosti_vyuctovani_zdarma.pdf',
	skippayConditionsForFirstPurchaseDiscount:
		'/files/cs/Skippay_Podminky_pro_uplatneni_slevy_na_prvni_nakup.pdf',
	contractInstallments: '/files/cs/Skippay_Vzor_Smlouvy_ke_Splatkam.pdf',

	preContractualInformationInstallmentsExample:
		'/files/cs/Skippay_Vzor_Predsmluvnich_informaci_ke_Splatkam.pdf',
	preContractualInformationInstallments: '/files/cs/Skippay_Predsmluvni_informace_Splatky.pdf',
	infoPermanentlyAccessibleToConsumerInstallments:
		'files/cs/Skippay_Informace_trvale_pristupne_spotrebiteli.pdf',
	skippayRepresentativeExample: '/files/cs/Skippay_reprezentativni_priklad.pdf',
	skippayGeneralTermsAndConditions: '/files/cs/Skippay_Vseobecne_obchodni_podminky.pdf',
	sPConditionsDiscount12100CZKOnBilling:
		'/files/cs/SP_Podminky_akce_Sleva_12100_Kc_na_vyuctovani.pdf',
	IPID: 'files/IPID.pdf',
	leafletYourRightsPaymentsEUCS: 'files/leaflet-your-rights-payments-eu_cs.pdf',
};

const SK_FILES = {
	cashbackRules: '',
	currentPricingDocument: '/files/sk/Skippay_Cennik.pdf',
	deprecatedPricingDocument: '',
	oldPricings: [],
	loyaltyProgramRules: '',
	insuranceTerms: '',
	termsAccessibility: '',
	termsAndConditions: '',
	termsAndConditionsDeferredPayment: '',
	termsAndConditionsPayInThree: '/files/sk/Skippay_Podmienky_nakupu_na_tretinu_SK.pdf',
	termsAndConditionsMini: '/files/sk/Skippay_Podmienky_Balicka_MINI.pdf',
	termsAndConditionsMaxi: '',
	termsAndConditionsMaxiDeprecated: '',
	termsAndConditionsMaxiCurrent: '',
	termsAndConditionsMaxiSampleContract: '',
	termsAndConditionsMaxiSamplePreContractualInformation: '',
	oldTermsAndConditions: [],
	oldTermsAndConditionsPayInThree: [...SK_OLD_TERMS_AND_CONDITIONS_PAY_IN_THREE],
	oldTermsAndConditionsDeferredPayment: [],
	oldTermsAndConditionsMini: [],
	oldTermsAndConditionsMaxi: [],
	oldCardTermsAndConditions: [],
	informationMemorandum: '/files/sk/Skippay_Informacni_memorandum.pdf',
	informationMemorandumPartners: '/files/sk/Skippay_Informacni_memorandum_partneri.pdf',
	informationMemorandumSID: '',
	informationMemorandumNRKI: '',
	informationMemorandumCareer: '',
	personalDataProtection: 'https://www.mcribis.cz/ochrana-osobnich-udaju/',
	NRKIInformationMemorandum: '',
	conditionsForObtainingExtraRewardPoints: '',
	conditionsFor12x100: '',
	complaintsProcedure: '/files/sk/Skippay_Reklamacny_poriadok.pdf',
	yourRightsWhenMakingPaymentsInEurope: '',
	xmasConditions: '',
	springConditions: '',
	deprecatedInsurance: {
		insurance: {
			conditions: '',
			ipid: '',
			infolist: '',
		},
		extendedWarranty: {
			conditions: '',
			ipid: '',
			infolist: '',
		},
	},
	ethicalCodex: '/files/sk/Skippay_Eticky_kodex.pdf',
	criminalActivity: '/files/sk/Skippay_Nulova_tolerancia_k_trestnej_cinnosti.pdf',
	conditionsPromoTryItForFree: '',
	contractInstallments: '',
	preContractualInformationInstallmentsExample: '',
	preContractualInformationInstallments: '',
	infoPermanentlyAccessibleToConsumerInstallments: '',
};

const HU_FILES = {
	cashbackRules: '',
	currentPricingDocument: '/files/hu/pricing.pdf',
	deprecatedPricingDocument: '',
	oldPricings: [],
	loyaltyProgramRules: '',
	insuranceTerms: '',
	termsAccessibility: '',
	termsAndConditions: '',
	termsAndConditionsDeferredPayment: '',
	termsAndConditionsPayInThree: '/files/hu/terms_and_conditions_pay_in_three.pdf',
	termsAndConditionsMini: '/files/hu/terms_and_conditions_MINI.pdf',
	termsAndConditionsMaxi: '',
	termsAndConditionsMaxiDeprecated: '',
	termsAndConditionsMaxiCurrent: '',
	termsAndConditionsMaxiSampleContract: '',
	termsAndConditionsMaxiSamplePreContractualInformation: '',
	oldTermsAndConditions: [],
	oldTermsAndConditionsPayInThree: [],
	oldTermsAndConditionsDeferredPayment: [],
	oldTermsAndConditionsMini: [],
	oldTermsAndConditionsMaxi: [],
	oldCardTermsAndConditions: [],
	informationMemorandum: '/files/hu/information_memorandum.pdf',
	informationMemorandumPartners: '/files/hu/partners_information_memorandum.pdf',
	informationMemorandumSID: '',
	informationMemorandumNRKI: '',
	informationMemorandumCareer: '',
	personalDataProtection: 'https://www.mcribis.cz/ochrana-osobnich-udaju/',
	NRKIInformationMemorandum: '',
	conditionsForObtainingExtraRewardPoints: '',
	conditionsFor12x100: '',
	complaintsProcedure: '/files/hu/complaints_policy.pdf',
	yourRightsWhenMakingPaymentsInEurope: '',
	xmasConditions: '',
	springConditions: '',
	deprecatedInsurance: {
		insurance: {
			conditions: '',
			ipid: '',
			infolist: '',
		},
		extendedWarranty: {
			conditions: '',
			ipid: '',
			infolist: '',
		},
	},
	ethicalCodex: '/files/hu/code_of_ethics.pdf',
	criminalActivity: '/files/hu/zero_tolerance_policy.pdf',
	conditionsPromoTryItForFree: '',
	contractInstallments: '',
	preContractualInformationInstallmentsExample: '',
	preContractualInformationInstallments: '',
	infoPermanentlyAccessibleToConsumerInstallments: '',
};

export const FILES = (locale: Locale = process.env.NEXT_PUBLIC_LANGUAGE || 'cs') => {
	switch (locale) {
		case 'cs':
			return CS_FILES;
		case 'sk':
			return SK_FILES;
		case 'hu':
			return HU_FILES;
		default:
			throw new Error('Locale is not supported yet');
	}
};
